import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import { formatTurkeyLocalDateTime } from '../../../utils/turkeyLocalDateTime';

const formatUtcAuditDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MeetingReportDialog = ({ open, onClose, data }) => {
  if (!data) return null;

  const participants = data.participants || [];
  const guests = data.guests || [];
  const summary = data.summary || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Katılım Raporu
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2, px: 3 }}>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {/* Toplantı bilgisi */}
          <Box>
            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', mb: 1.5 }}>
              {data.meetingTitle || '-'}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {data.meetingDate && (
                <Chip
                  icon={<ScheduleIcon sx={{ fontSize: '16px !important' }} />}
                  label={formatTurkeyLocalDateTime(data.meetingDate)}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '12px',
                    fontWeight: 600,
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: '#f5f5f5',
                    color: '#444444',
                    '& .MuiChip-icon': { color: '#a3a3a3' },
                  }}
                />
              )}
              {data.location && (
                <Chip
                  icon={<LocationOnIcon sx={{ fontSize: '16px !important' }} />}
                  label={data.location}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '12px',
                    fontWeight: 600,
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: '#f5f5f5',
                    color: '#444444',
                    '& .MuiChip-icon': { color: '#a3a3a3' },
                  }}
                />
              )}
            </Stack>
          </Box>

          {/* Özet */}
          {Object.keys(summary).length > 0 && (
            <>
              <Box>
                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 1 }}>
                  Özet
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <Chip
                    size="small"
                    label={`Toplam: ${summary.totalParticipants ?? 0}`}
                    sx={{ height: 24, fontSize: '12px', fontWeight: 600, borderRadius: '12px', border: 'none', backgroundColor: '#f5f5f5', color: '#444444' }}
                  />
                  <Chip
                    size="small"
                    label={`Gelen: ${summary.attendedParticipantsCount ?? 0}`}
                    sx={{ height: 24, fontSize: '12px', fontWeight: 600, borderRadius: '12px', border: 'none', backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                  />
                  <Chip
                    size="small"
                    label={`Gelmeyen: ${summary.absentParticipantsCount ?? 0}`}
                    sx={{ height: 24, fontSize: '12px', fontWeight: 600, borderRadius: '12px', border: 'none', backgroundColor: '#ffebee', color: '#c62828' }}
                  />
                  <Chip
                    size="small"
                    label={`İzinli: ${summary.exemptCount ?? 0}`}
                    sx={{ height: 24, fontSize: '12px', fontWeight: 600, borderRadius: '12px', border: 'none', backgroundColor: '#fff3e0', color: '#e65100' }}
                  />
                  <Chip
                    size="small"
                    label={`Misafir: ${summary.totalGuests ?? 0}`}
                    sx={{ height: 24, fontSize: '12px', fontWeight: 600, borderRadius: '12px', border: 'none', backgroundColor: '#e8eaf6', color: '#3949ab' }}
                  />
                  <Chip
                    size="small"
                    label={`Gelen misafir: ${summary.attendedGuestsCount ?? 0}`}
                    sx={{ height: 24, fontSize: '12px', fontWeight: 600, borderRadius: '12px', border: 'none', backgroundColor: '#f5f5f5', color: '#444444' }}
                  />
                  <Chip
                    size="small"
                    label={`Katılım: %${Number(summary.attendanceRate ?? 0).toFixed(0)}`}
                    sx={{ height: 24, fontSize: '12px', fontWeight: 600, borderRadius: '12px', border: 'none', backgroundColor: '#f5f5f5', color: '#444444' }}
                  />
                </Stack>
              </Box>
              <Divider sx={{ borderColor: '#e6e6e6' }} />
            </>
          )}

          {/* Katılımcılar */}
          <Box>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1.5 }}>
              <PeopleIcon sx={{ fontSize: 20, color: '#0E4D94' }} />
              <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                Katılımcılar ({participants.length})
              </Typography>
            </Stack>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none', borderRadius: 0 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ad Soyad</TableCell>
                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Tür</TableCell>
                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İzinli</TableCell>
                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Check-in</TableCell>
                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Misafir</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3, fontSize: '14px', color: '#a3a3a3' }}>
                        Katılımcı yok
                      </TableCell>
                    </TableRow>
                  ) : (
                    participants.map((p, idx) => (
                      <TableRow
                        key={p.attendanceId || p.personId}
                        sx={{
                          '& .MuiTableCell-root': {
                            borderBottom: idx === participants.length - 1 ? 'none' : '1px dashed #e8e8e8',
                            py: 1,
                            fontSize: '14px',
                            color: '#444444',
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#444444' }}>
                            {p.fullName ?? '-'}
                          </Typography>
                          {p.parentFullName && (
                            <Typography variant="caption" sx={{ fontSize: '12px', color: '#a3a3a3', display: 'block', mt: 0.25 }}>
                              Üye: {p.parentFullName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={p.typeName ?? (p.type === 1 ? 'Yakın' : 'Üye')}
                            sx={{
                              height: 24,
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: p.type === 1 ? '#FFF4E6' : '#E7EDF4',
                              color: p.type === 1 ? '#FF9800' : '#0E4D94',
                              borderRadius: '12px',
                              border: 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell>{p.statusName ?? '-'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={p.isExempt ? 'Evet' : 'Hayır'}
                            sx={{
                              height: 24,
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: p.isExempt ? '#DFFFEA' : '#f5f5f5',
                              color: p.isExempt ? '#17C653' : '#666666',
                              borderRadius: '12px',
                              border: 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell>{p.checkedInAt ? formatUtcAuditDate(p.checkedInAt) : '-'}</TableCell>
                        <TableCell>
                          {(p.guestCount ?? 0) > 0 ? (
                            <Chip
                              size="small"
                              label={p.guestCount ?? 0}
                              sx={{
                                height: 24,
                                fontSize: '12px',
                                fontWeight: 600,
                                backgroundColor: '#E7EDF4',
                                color: '#0E4D94',
                                borderRadius: '12px',
                                border: 'none',
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Misafirler */}
          {guests.length > 0 && (
            <Box>
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1.5 }}>
                <PersonIcon sx={{ fontSize: 20, color: '#0E4D94' }} />
                <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                  Misafirler ({guests.length})
                </Typography>
              </Stack>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none', borderRadius: 0 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'transparent' }}>
                    <TableRow>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ad Soyad</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Getiren</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Telefon</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Check-in</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {guests.map((g, idx) => (
                      <TableRow
                        key={g.guestId}
                        sx={{
                          '& .MuiTableCell-root': {
                            borderBottom: idx === guests.length - 1 ? 'none' : '1px dashed #e8e8e8',
                            py: 1,
                            fontSize: '14px',
                            color: '#444444',
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#444444' }}>
                            {g.fullName || [g.firstName, g.lastName].filter(Boolean).join(' ') || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{g.hostFullName ?? '-'}</TableCell>
                        <TableCell>{g.phoneNumber ?? '-'}</TableCell>
                        <TableCell>{g.statusName ?? '-'}</TableCell>
                        <TableCell>{g.checkedInAt ? formatUtcAuditDate(g.checkedInAt) : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {data.updatedAt && (
            <Typography variant="caption" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
              Son güncelleme: {formatUtcAuditDate(data.updatedAt)}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            color: '#666666',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeetingReportDialog;
